import * as React from "react";
import { useState } from "react";
import * as style from "./navMobile.module.scss";
import { v4 as uuidv4 } from "uuid";
import { useI18next, useTranslation } from "gatsby-plugin-react-i18next";
import { navigate } from "gatsby";
import { navOptions, searchEverythingPath } from "./config.js";
import Link from "components/Link";
import Accordion from "components/Accordion";


import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import logoCitius from "images/corporative/logo_citius_tagline_horizontal.svg";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { modifyUrlWithLanguage } from 'src/utils/urlUtils';

const NavSearch = ({ searchValue, closeNavMobile }) => {
  const { t } = useTranslation();
  const { language } = useI18next();

  function doSearch(event) {
    event.preventDefault();
    const value = event.currentTarget.elements.namedItem("search").value;
    const path = modifyUrlWithLanguage(searchEverythingPath, language);
    navigate(path + "?search=" + encodeURI(value));
    closeNavMobile();
  }
  return (
    <form className={style.NavSearch} onSubmit={doSearch} role="search">
      <label htmlFor="GlobalSearchInput" className={style.NavSearch_icon}>
        <SearchIcon aria-hidden="true" />
      </label>
      <input
        id="GlobalSearchInput"
        name="search"
        type="search"
        aria-label={t("search")}
        placeholder={`${t("search")}...`}
        defaultValue={searchValue || ""}
      />
      <button
        type="submit"
        className={style.NavSearch_submit}
        aria-label={t("search")}
        title={t("search")}
      >
        <ArrowForwardIcon />
      </button>
    </form>
  );
};

const LanguageSelectorAndSearch = ({ closeNavMobile }) => {
  const { language, languages, changeLanguage } = useI18next();
  return (
    <div className={style.LanguageSelector}>
      <NavSearch closeNavMobile={closeNavMobile} />
      {languages.map((lng) => {
        const isCurrentLanguage = lng === language;

        return (
          <button
            key={lng}
            className={
              isCurrentLanguage ? style.LanguageSelector_current : undefined
            }
            aria-pressed={isCurrentLanguage}
            onClick={(e) => {
              e.preventDefault();
              closeNavMobile();
              if (!isCurrentLanguage) {
                changeLanguage(lng);
              }
            }}
          >
            {lng}
          </button>
        );
      })}
    </div>
  );
};

const NavMenu = ({ closeNavMobile, isOpen, onCloseAnimationEnd }) => {
  const { t } = useTranslation();
  const { language, defaultLanguage } = useI18next();
  
  return (
    <div
      className={`${style.NavMenu} ${isOpen ? "" : style.NavMenu___closing}`}
      aria-hidden={!isOpen}
      inert={!isOpen}
      onAnimationEnd={(event) => {
        if (!isOpen && event.target === event.currentTarget) {
          onCloseAnimationEnd();
        }
      }}
    >
      {navOptions.map((option, index) => {
        if (option.submenu) {
          return (
            <Accordion
              key={uuidv4()}
              defaultClosed
              title={t(`${option.name}`)}
              className={style.NavMenu_accordion}
              isMobileMenu={true}
              content={
                <>
                  {option.submenu.map((suboption) => (
                    <Link
                      to={modifyUrlWithLanguage(suboption.link, language, defaultLanguage)}
                      key={uuidv4()}
                      onClick={closeNavMobile}
                    >
                      {t(`${suboption.name}`)}
                    </Link>
                  ))}
                </>
              }
            />
          );
        }
        return (
          <Link
            to={modifyUrlWithLanguage(option.link, language, defaultLanguage)}
            key={option.link}
            className={style.NavMenu_link}
            onClick={closeNavMobile}
          >
            {t(`${option.name}`)}
          </Link>
        );
      })}
      <LanguageSelectorAndSearch closeNavMobile={closeNavMobile} />
    </div>
  );
};

const NavMobile = () => {
  const navRef = React.useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isMenuMounted, setIsMenuMounted] = useState(false);
  const closeNavMobile = React.useCallback(() => {
    setIsOpen(false);
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setIsMenuMounted(false);
    }
  }, []);
  const toggleNavMobile = () => {
    if (isOpen) {
      closeNavMobile();
    } else {
      setIsMenuMounted(true);
      setIsOpen(true);
    }
  };

  React.useEffect(() => {
    if (isOpen || !isMenuMounted) return undefined;
    const timeout = window.setTimeout(() => setIsMenuMounted(false), 220);
    return () => window.clearTimeout(timeout);
  }, [isOpen, isMenuMounted]);
  React.useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnOutsidePointer = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        closeNavMobile();
      }
    };
    document.addEventListener("pointerdown", closeOnOutsidePointer);
    return () => document.removeEventListener("pointerdown", closeOnOutsidePointer);
  }, [isOpen, closeNavMobile]);
  const { t } = useTranslation();
  return (
    <nav ref={navRef} className={style.NavMobile}>
      <div className={style.NavMobile_navBar}>
        <Link to="/" className={style.NavMobile_logo} aria-label={t("mainPage")}>
          <img src={logoCitius} alt="" />
        </Link>
        <button
          className={style.NavMobile_menuButton}
          onClick={toggleNavMobile}
          aria-label={isOpen ? t("closeMenu") : t("openMenu")}
        >
          {isOpen ? <CloseIcon /> : <MenuIcon />}
        </button>
      </div>
      {isMenuMounted && (
        <NavMenu
          closeNavMobile={closeNavMobile}
          isOpen={isOpen}
          onCloseAnimationEnd={() => setIsMenuMounted(false)}
        />
      )}
    </nav>
  );
};

export default NavMobile;
